import moment from "moment";
import { text } from "../utils/i18n";
import { LeadListMode } from "./LeadListMode";

export class CallStatisticsItem {
  readonly userId: string;
  readonly callCountArgument: number;
  readonly callCountTotal: number;
  readonly callCountPercentArgument: number;
  readonly connectedDuration: number;
  readonly averageConnectedDuration: number;
  readonly userStatus: UserCallStatus;
  readonly userStatusSince?: Date;
  readonly userStatusDuration?: number; //  Property exists for sorting.

  //  Below: Joined from other data source.
  readonly userActiveDuration?: number;
  readonly userProjectId?: string;
  readonly userLeadListId?: string;
  readonly userLeadListMode?: LeadListMode;
  readonly cohearingUserId?: string;

  constructor(deriveFrom: Partial<CallStatisticsItem>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.userStatusSince) {
      this.userStatusSince = new Date(this.userStatusSince);
      this.userStatusDuration = Date.now() - this.userStatusSince.getTime();
    }
  }
}

export enum UserCallStatus {
  Inactive = 'inactive',
  PredictiveAwaitingCall = 'predictiveAwaitingCall',
  CohearingAwaitingCall = 'cohearingAwaitingCall',
  Connected = 'connected',
  Connecting = 'connecting',
  EvaluatingCall = 'evaluatingCall',
  Cohearing = 'cohearing',
}

type GetUserCallStatusTextParams = {
  userName: string;
  status: UserCallStatus;
  cohearingUserName?: string;
  since?: Date;
}

export function getUserCallStatusText({ status, userName, cohearingUserName, since }: GetUserCallStatusTextParams) {
  const duration = since ? moment(since).fromNow(true) : null;

  if (!duration) {
    switch (status) {
      case UserCallStatus.Connected:
        return text`${userName} är i samtal`;

      case UserCallStatus.Connecting:
        return text`${userName} ringer`;

      case UserCallStatus.EvaluatingCall:
        return text`${userName} väljer utfall`;

      case UserCallStatus.Cohearing:
        return cohearingUserName ? text`${userName} medlyssnar ${cohearingUserName}` :
          text`${userName} medlyssnar`;

      case UserCallStatus.PredictiveAwaitingCall:
        return text`${userName} ringer prediktivt och inväntar samtal`;

      case UserCallStatus.CohearingAwaitingCall:
        return cohearingUserName ? text`${userName} medlyssnar ${cohearingUserName} och inväntar samtal` :
          text`${userName} medlyssnar och inväntar samtal`;

      case UserCallStatus.Inactive:
        return text`${userName} har ingen samtalsaktivitet just nu`;

      default:
        return null;
    }
  }

  switch (status) {
    case UserCallStatus.Connected:
      return text`${userName} har varit i samtal i ${duration}`;

    case UserCallStatus.Connecting:
      return text`${userName} har ringt i ${duration}`;

    case UserCallStatus.EvaluatingCall:
      return text`${userName} har valt utfall i ${duration}`;

    case UserCallStatus.PredictiveAwaitingCall:
      return text`${userName} ringer prediktivt och har inväntat samtal i ${duration}`;

    case UserCallStatus.CohearingAwaitingCall:
      return cohearingUserName ? text`${userName} medlyssnar ${cohearingUserName} och har inväntat samtal i ${duration}` :
        text`${userName} medlyssnar och har inväntat samtal i ${duration}`;

    case UserCallStatus.Inactive:
      return text`${userName} har inte haft samtalsaktivitet på ${duration}`;

    case UserCallStatus.Cohearing:
      return cohearingUserName ? text`${userName} har medlyssnat ${cohearingUserName} i ${duration}` :
        text`${userName} har medlyssnat i ${duration}`;

    default:
      return null;
  }
}

/* Ordning av aktivitet enligt Verbal AB.
 * Viktor:
 * https://salesys.slack.com/archives/DFJA9TXAN/p1743155840842549
 * 
 * I samtal
 * Väljer utfall
 * Inväntar samtal
 * Medlyssnar
 * Ingen
 */
export const userCallStatusPosition: UserCallStatus[][] = [
  [UserCallStatus.Connected, UserCallStatus.Connecting],
  [UserCallStatus.EvaluatingCall],
  [UserCallStatus.PredictiveAwaitingCall],
  [UserCallStatus.Cohearing],
  [UserCallStatus.CohearingAwaitingCall],
  [UserCallStatus.Inactive]
];